import {H2} from '@jetbrains/ring-ui/components/heading/heading'
import {skipToken} from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import * as React from 'react'

import EntityLink from '../../../../../containers/EntityLink'
import {getSingleProjectArg} from '../../../../../rest/projects'
import {restApi} from '../../../../../services/rest'
import type {ProjectId} from '../../../../../types'
import Description from '../../../Description/Description.container'
import Details from '../../../Details/Details'
import FavoriteStar from '../../../FavoriteStar/FavoriteStar.container'
import MiddleEllipsis from '../../../MiddleEllipsis/MiddleEllipsis'
import OverviewStatusIcon from '../../../ProjectOrBuildTypeIcon/OverviewStatusIcon.container'
import {EntityIconType} from '../../../ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon.types'
import {useSequenceLoaderReadyHandler} from '../../../SequenceLoader/SequenceLoader.hooks'
import SvgIcon from '../../../SvgIcon/SvgIcon'
import {NestedSubproject} from '../../NestedSubprojectsAndBuildTypesContext'

import SubprojectBreadcrumbs from './Subproject.breadcrumbs'
import SubprojectActions from './Subproject.details'

import styles from './Subproject.css'

type Props = {
  hasBuildTypes?: boolean
  hasSubprojects?: boolean
  projectId: ProjectId
  isArchived?: boolean
  isExpanded?: boolean
  onlyFavorites?: boolean
  wasVisible?: boolean
  toggleSubproject?: (arg0: boolean) => unknown
  name?: string
  level?: number
}

type SubprojectEntityProps = {
  projectId: ProjectId
  name?: string
}

const SubprojectEntity = React.memo(({projectId, name}: SubprojectEntityProps) => {
  if (name == null) {
    return null
  }

  return (
    <H2 className={styles.entityPath}>
      <OverviewStatusIcon type={EntityIconType.PROJECT} id={projectId} size="M" />
      <EntityLink title={name} projectId={projectId} className={styles.link}>
        <MiddleEllipsis tailLength={10}>{name}</MiddleEllipsis>
      </EntityLink>
    </H2>
  )
})
SubprojectEntity.displayName = 'SubprojectEntity'

function Subproject({
  projectId,
  isArchived = false,
  onlyFavorites = false,
  isExpanded = false,
  toggleSubproject,
  name,
  level = 0,
  hasBuildTypes = false,
  hasSubprojects = false,
  wasVisible = false,
}: Props) {
  const summaryRef = React.useRef(null)
  const hasChildren = hasBuildTypes || hasSubprojects

  restApi.endpoints.getProjectNormalized.useQuerySubscription(
    wasVisible === true ? getSingleProjectArg(projectId, {withDescription: true}) : skipToken,
  )

  const onToggle = React.useCallback(
    (arg0: boolean) => {
      if (hasChildren && typeof toggleSubproject === 'function') {
        toggleSubproject(arg0)
      }
    },
    [hasChildren, toggleSubproject],
  )
  const summary = React.useMemo(
    () => (
      <div ref={summaryRef} className={styles.summaryWrapper}>
        <SubprojectBreadcrumbs
          level={level}
          showFullBreadcrumbs={onlyFavorites}
          projectId={projectId}
        />
        <div
          className={styles.header}
          data-subproject-header
          data-test="subproject"
          data-project-id={projectId}
        >
          <div className={styles.entity}>
            <div className={styles.entityContainer}>
              <SubprojectEntity name={name} projectId={projectId} />
            </div>
            {!isArchived && <FavoriteStar className={styles.star} projectId={projectId} />}
            <Description className={styles.description} projectId={projectId} lines={1} />
          </div>
          <SubprojectActions projectId={projectId} hasChildren={hasChildren} />
          {hasChildren ? (
            <SvgIcon
              className={styles.arrow}
              icon={isExpanded ? 'chevron-down' : 'chevron-right'}
            />
          ) : null}
        </div>
      </div>
    ),
    [hasChildren, isArchived, isExpanded, level, name, onlyFavorites, projectId],
  )

  useSequenceLoaderReadyHandler(!isExpanded || !hasChildren)

  return (
    <div className={classNames(styles.container)}>
      <Details
        open={isExpanded}
        onToggle={onToggle}
        renderButtonAside
        imitateHeading
        level={2}
        label={name ?? 'Project'}
        summary={summary}
        headingClassName={classNames(styles.line, !hasChildren && styles.noChildren)}
      >
        <div className={classNames(styles.details, styles.nested)}>
          {hasChildren ? <NestedSubproject level={level} projectId={projectId} /> : null}
        </div>
      </Details>
    </div>
  )
}

export default Subproject
